import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Check, Menu, X, ArrowRight, Phone, Users, Building2, Sparkles, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-lg text-slate-900">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/product" className="text-slate-600 hover:text-slate-900 text-sm">Product</Link>
            <Link to="/pricing" className="text-emerald-700 font-medium text-sm">Pricing</Link>
            <Link to="/about" className="text-slate-600 hover:text-slate-900 text-sm">About</Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 text-sm">Contact</Link>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" size="sm">Log in</Button></Link>
            <Link to="/signup"><Button size="sm" className="bg-emerald-700 hover:bg-emerald-800">Start Free Trial</Button></Link>
          </div>
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {isOpen && (
          <div className="md:hidden py-4 border-t border-slate-100">
            <div className="flex flex-col gap-3">
              <Link to="/product" className="text-slate-600 py-2">Product</Link>
              <Link to="/pricing" className="text-emerald-700 font-medium py-2">Pricing</Link>
              <Link to="/about" className="text-slate-600 py-2">About</Link>
              <Link to="/contact" className="text-slate-600 py-2">Contact</Link>
              <div className="pt-3 flex flex-col gap-2">
                <Link to="/login"><Button variant="outline" className="w-full">Log in</Button></Link>
                <Link to="/signup"><Button className="w-full bg-emerald-700 hover:bg-emerald-800">Start Free Trial</Button></Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const InfoTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help inline ml-1" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-xs">{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const PricingPage = () => {
  const plans = [
    {
      name: "Starter",
      price: 49,
      period: "month",
      description: "For small teams handling daily WhatsApp enquiries.",
      icon: Phone,
      features: [
        { text: "1 WhatsApp channel", tooltip: "Each channel connects one WhatsApp Business API number." },
        { text: "3 team members" },
        { text: "Structured enquiry flows" },
        { text: "Human handover" },
        { text: "Conversation tracking" },
        { text: "Basic analytics" }
      ],
      popular: false,
      planId: "starter"
    },
    {
      name: "Growth",
      price: 149,
      period: "month",
      description: "For growing teams managing higher enquiry volume.",
      icon: Users,
      features: [
        { text: "Up to 3 WhatsApp channels", tooltip: "Each channel connects one WhatsApp Business API number." },
        { text: "10 team members" },
        { text: "Smart structured intake flows" },
        { text: "Team inbox" },
        { text: "Analytics dashboard" },
        { text: "Priority support" }
      ],
      popular: true,
      planId: "growth"
    },
    {
      name: "Scale",
      price: 499,
      period: "month",
      description: "For multi-location or high-volume businesses.",
      icon: Building2,
      features: [
        { text: "Up to 10 WhatsApp channels", tooltip: "Each channel connects one WhatsApp Business API number." },
        { text: "30 team members" },
        { text: "Multi-team access" },
        { text: "Operational analytics" },
        { text: "High-volume AI credits" },
        { text: "Priority support" }
      ],
      popular: false,
      planId: "scale"
    }
  ];

  const enterprisePlan = {
    name: "Enterprise",
    description: "For large organisations with custom requirements.",
    icon: Sparkles,
    features: [
      { text: "Custom WhatsApp channels" },
      { text: "Custom team size" },
      { text: "Custom AI credit allocation" },
      { text: "Dedicated onboarding" },
      { text: "Integration support" },
      { text: "SLA guarantee" }
    ]
  };

  const whyFlowteras = [
    {
      text: "No more missed WhatsApp enquiries",
      description: "Every message is captured, tracked, and responded to."
    },
    {
      text: "No messy booking conversations",
      description: "Structured flows guide customers through clear steps."
    },
    {
      text: "Keep full control — escalates when needed",
      description: "Your team handles the conversations that matter most."
    },
    {
      text: "Works alongside your existing team",
      description: "Enhances your workflow without replacing your people."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-emerald-700 font-medium text-sm mb-3">Simple Pricing</p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4" data-testid="pricing-title">
            Choose the right plan for your team
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Scale your WhatsApp automation as your business grows. All plans include full AI capabilities.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <div 
                key={i} 
                className={`relative bg-white rounded-xl p-6 ${
                  plan.popular 
                    ? 'border-2 border-emerald-600 shadow-lg shadow-emerald-100' 
                    : 'border border-slate-200'
                }`}
                data-testid={`pricing-plan-${plan.planId}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                
                <div className="mb-5">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${
                    plan.popular ? 'bg-emerald-100' : 'bg-slate-100'
                  }`}>
                    <plan.icon className={`w-5 h-5 ${plan.popular ? 'text-emerald-700' : 'text-slate-600'}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{plan.name}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-slate-900">${plan.price}</span>
                    <span className="text-slate-500 text-sm">/{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, j) => (
                    <li key={j} className={`flex items-start gap-2.5 text-sm ${feature.highlight ? 'text-emerald-700 font-medium' : 'text-slate-600'}`}>
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.popular || feature.highlight ? 'text-emerald-600' : 'text-slate-400'}`} />
                      <span>
                        {feature.text}
                        {feature.tooltip && <InfoTooltip content={feature.tooltip} />}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to="/signup">
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-slate-900 hover:bg-slate-800'}`}
                    data-testid={`pricing-cta-${plan.planId}`}
                  >
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          {/* Enterprise Card */}
          <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-8 text-white" data-testid="pricing-plan-enterprise">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <enterprisePlan.icon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold">{enterprisePlan.name}</h3>
                </div>
                <p className="text-slate-300 mb-4">{enterprisePlan.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {enterprisePlan.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span className={feature.highlight ? 'text-emerald-400' : ''}>{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-white text-slate-900 hover:bg-slate-100"
                    data-testid="pricing-cta-enterprise"
                  >
                    Contact Sales
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mt-8 text-center space-y-2">
            <p className="text-slate-500 text-sm">
              14-day free trial. No credit card required.
            </p>
            <p className="text-slate-400 text-xs">
              WhatsApp conversation fees are billed directly by Meta.
            </p>
          </div>
        </div>
      </section>

      {/* All AI Features Note */}
      <section className="py-8 px-4 bg-emerald-50 border-y border-emerald-100">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <span className="font-semibold text-emerald-800">All AI features are included in every plan</span>
          </div>
          <p className="text-emerald-700 text-sm">
            Smart auto-replies, FAQ automation, structured intake flows, sentiment analysis, and more.
          </p>
        </div>
      </section>

      {/* Why Flowteras */}
      <section className="py-16 px-4 bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-center text-slate-900 mb-10" data-testid="why-flowteras-title">
            Why Flowteras?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {whyFlowteras.map((item, i) => (
              <div 
                key={i} 
                className="bg-white rounded-lg p-5 border border-slate-200"
                data-testid={`why-flowteras-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 mb-1">{item.text}</p>
                    <p className="text-slate-500 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-semibold text-slate-900 mb-3">
            Ready to organise your WhatsApp enquiries?
          </h2>
          <p className="text-slate-600 mb-6">
            Start your free trial today. No setup required.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800" data-testid="pricing-cta-bottom">
              Start Free Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Simple FAQ */}
      <section className="py-16 px-4 bg-slate-50 border-t border-slate-100">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl font-semibold text-center text-slate-900 mb-8">
            Common Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "What is a WhatsApp Channel?",
                a: "Each channel connects one WhatsApp Business API number. If you have multiple business lines or locations, you'll need multiple channels."
              },
              {
                q: "How do AI credits work?",
                a: "Credits are consumed per AI interaction cycle - when AI processes an incoming message and generates a response. Each cycle uses 5 credits."
              },
              {
                q: "Can I upgrade or downgrade anytime?",
                a: "Yes! You can change your plan at any time. Upgrades take effect immediately, and downgrades apply at your next billing cycle."
              },
              {
                q: "What happens if I exceed my limits?",
                a: "You'll receive warnings as you approach limits. If you exceed your message or credit limit, automation pauses until you upgrade or your plan resets."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-lg p-4 border border-slate-200">
                <p className="font-medium text-slate-900 mb-1">{faq.q}</p>
                <p className="text-slate-500 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
